import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ApiService, VersionRedressmentService } from "../../../api";
import { translate } from "../../providers";
import SmartReviewContext from "../../../views/my-review/context/smart-review-context";
import { NavUtils } from "../../../navigation";
import { CustomButton, CustomButtonLink, CustomIconButton, SidePanel, icon } from "..";
import ComparisonDocumentViewer from "./ComparisonDocumentViewer";
import styles from "./VersionComparisonPanel.module.css";
import { debounce } from "../../utils";

const SIDE_PANEL_WIDTH = 95;
const debouncedFunction = debounce((func) => func(), 1000);
export default function VersionComparisonPanel({
	open,
	onClose,
	currentDocId,
	currentDocName,
	previousDocId,
	previousDocName,
	projectId,
	companyId,
	enableLeftApiCall = true,
	enableRightApiCall = false,
	initialLeftPage = null,
	initialRightPage = null,
	initialLeftInfoId = null,
	initialRightInfoId = null,
}) {
	const location = useLocation();
	const cancelTokenSourceRef = useRef(null);
	const leftScrollRef = useRef(null);
	const rightScrollRef = useRef(null);
	const disableSyncRef = useRef(false);

	const [scrollLocked, setScrollLocked] = useState(() => {
		const savedPreferences = SmartReviewContext.getContext()?.preferences;
		return savedPreferences?.comparisonScrollLock ?? true;
	});
	const [leftPageDimensions, setLeftPageDimensions] = useState([]);
	const [rightPageDimensions, setRightPageDimensions] = useState([]);
	const [sharedZoom, setSharedZoom] = useState(100);

	const [leftScrollTarget, setLeftScrollTarget] = useState(null);
	const [rightScrollTarget, setRightScrollTarget] = useState(null);
	const [leftInitialSelectedContent, setLeftInitialSelectedContent] = useState(null);
	const [rightInitialSelectedContent, setRightInitialSelectedContent] = useState(null);
	const initialScrollAppliedRef = useRef(false);
	const handleReturnToContent = useCallback(() => {
		const scrollToGreen = (containerRef) => {
			const container = containerRef?.current;
			if (!container) {
				return;
			}
			// eslint-disable-next-line quotes
			const greenElement = container.querySelector('[data-initial-selected="true"]');
			if (!greenElement) {
				return;
			}
			const containerRect = container.getBoundingClientRect();
			const elementRect = greenElement.getBoundingClientRect();

			const elementTop = elementRect.top - containerRect.top + container.scrollTop;
			const elementHeight = elementRect.height;
			const containerHeight = containerRect.height;

			const targetScroll = elementTop - containerHeight / 2 + elementHeight / 2;

			container.scrollTo({
				top: Math.max(0, targetScroll),
				behavior: "smooth",
			});
		};

		disableSyncRef.current = true;

		scrollToGreen(leftScrollRef);
		scrollToGreen(rightScrollRef);

		setTimeout(() => {
			disableSyncRef.current = false;
		}, 800);
	}, []);
	useEffect(() => {
		cancelTokenSourceRef.current = ApiService.getCancelTokenSource();
		return () => {
			ApiService.cancelTokens(cancelTokenSourceRef.current);
		};
	}, []);

	useEffect(() => {
		SmartReviewContext.setContext({
			preferences: {
				...SmartReviewContext.getContext()?.preferences,
				comparisonScrollLock: scrollLocked,
			},
		});
	}, [scrollLocked]);

	useEffect(() => {
		if (!open) {
			setLeftInitialSelectedContent(null);
			setRightInitialSelectedContent(null);
			setLeftScrollTarget(null);
			setRightScrollTarget(null);
			initialScrollAppliedRef.current = false;
			return undefined;
		}

		if (initialScrollAppliedRef.current) {
			return undefined;
		}

		if (initialLeftInfoId) {
			setLeftInitialSelectedContent({ informationId: initialLeftInfoId });
		}
		if (initialRightInfoId) {
			setRightInitialSelectedContent({ informationId: initialRightInfoId });
		}

		const scrollTimeout = setTimeout(() => {
			initialScrollAppliedRef.current = true;
			if (initialLeftPage) {
				setLeftScrollTarget({
					page: initialLeftPage,
					informationId: initialLeftInfoId,
				});
			}
			if (initialRightPage) {
				setRightScrollTarget({
					page: initialRightPage,
					informationId: initialRightInfoId,
				});
			}
			debouncedFunction(() => handleReturnToContent());
		}, 300);

		return () => clearTimeout(scrollTimeout);
	}, [open, initialLeftPage, initialRightPage, initialLeftInfoId, initialRightInfoId, handleReturnToContent]);

	const handleClose = useCallback(() => {
		if (onClose) {
			onClose();
		}
	}, [onClose]);

	const handleToggleScrollLock = useCallback(() => {
		setScrollLocked((prev) => !prev);
	}, []);

	const handleLeftContentClick = useCallback(
		(contentArea, pageInfo) => {
			const informationId = contentArea?.informationId || contentArea?.info_id;
			if (!informationId) {
				return;
			}

			if (!enableLeftApiCall) {
				setLeftScrollTarget({
					informationId,
					page: contentArea?.pageStart || pageInfo?.page,
					y1: contentArea.y1,
				});

				setRightScrollTarget({
					informationId: null,
					page: contentArea?.pageStart || pageInfo?.page,
					y1: contentArea.y1,
				});
				return;
			}

			if (!cancelTokenSourceRef.current) {
				return;
			}

			VersionRedressmentService.getPreviousInformationDetails(
				{ informationId },
				cancelTokenSourceRef.current.token
			)
				.then((response) => {
					const { previousInfoId, previousPage } = response || {};

					setLeftScrollTarget({
						informationId,
						page: contentArea?.pageStart || pageInfo?.page,
						y1: contentArea.y1,
					});

					if (previousInfoId && previousPage) {
						setRightScrollTarget({
							informationId: previousInfoId,
							page: previousPage,
						});
					} else {
						setRightScrollTarget({
							informationId: null,
							page: contentArea?.pageStart || pageInfo?.page,
							y1: contentArea.y1,
						});
					}
				})
				.catch((error) => {
					console.error("Failed to get version redressment info:", error);
				});
		},
		[enableLeftApiCall]
	);

	const handleRightContentClick = useCallback(
		(contentArea, pageInfo) => {
			const informationId = contentArea?.informationId || contentArea?.info_id;
			if (!informationId) {
				return;
			}

			if (!enableRightApiCall) {
				setRightScrollTarget({
					informationId,
					page: contentArea?.pageStart || pageInfo?.page,
					y1: contentArea.y1,
				});

				setLeftScrollTarget({
					informationId: null,
					page: contentArea?.pageStart || pageInfo?.page,
					y1: contentArea.y1,
				});
				return;
			}

			if (!cancelTokenSourceRef.current) {
				return;
			}

			VersionRedressmentService.getPreviousInformationDetails(
				{ informationId },
				cancelTokenSourceRef.current.token
			)
				.then((response) => {
					const { previousInfoId, previousPage } = response || {};

					setRightScrollTarget({
						informationId,
						page: contentArea?.pageStart || pageInfo?.page,
						y1: contentArea.y1,
					});

					if (previousInfoId && previousPage) {
						setLeftScrollTarget({
							informationId: previousInfoId,
							page: previousPage,
						});
					} else {
						setLeftScrollTarget({
							informationId: null,
							page: contentArea?.pageStart || pageInfo?.page,
							y1: contentArea.y1,
						});
					}
				})
				.catch((error) => {
					console.error("Failed to get version redressment info:", error);
				});
		},
		[enableRightApiCall]
	);

	const handleLeftPageDimensionsChange = useCallback((dimensions) => {
		setLeftPageDimensions(dimensions);
	}, []);

	const handleRightPageDimensionsChange = useCallback((dimensions) => {
		setRightPageDimensions(dimensions);
	}, []);

	const handleZoomChange = useCallback((newZoom) => {
		setSharedZoom(newZoom);
	}, []);

	return (
		<SidePanel open={open} position="right" size={SIDE_PANEL_WIDTH} onClose={handleClose}>
			<div className={styles.sidePanelContainer}>
				<div className={styles.header}>
					<div className={styles.headerLeft}>
						<CustomIconButton icon={icon.faTimes} onClick={handleClose} />
						<span className={styles.headerTitle}>
							{translate("common:cmp-details.option-panel.versioning.comparison.title")}
						</span>
					</div>
					<div className={styles.headerRight}>
						{(initialLeftInfoId || initialRightInfoId) && (
							<CustomIconButton
								data-testid="panel.scroll.btn"
								icon={icon.faLocationCrosshairs}
								size="sm"
								tooltip={translate(
									"common:cmp-details.option-panel.versioning.comparison.return-to-content"
								)}
								variant="outlined"
								onClick={handleReturnToContent}
							/>
						)}
						<CustomButton
							className={styles.lockButton}
							color="secondary"
							size="md"
							startIcon={scrollLocked ? icon.faLock : icon.faLockOpen}
							variant="outlined"
							onClick={handleToggleScrollLock}
						>
							{scrollLocked
								? translate("common:cmp-details.option-panel.versioning.comparison.unlock-scroll")
								: translate("common:cmp-details.option-panel.versioning.comparison.lock-scroll")}
						</CustomButton>
					</div>
				</div>
				<div className={styles.comparisonContainer}>
					<ComparisonDocumentViewer
						isLatestVersion
						onlyShowContent
						showPageInfo
						showVersionInfo
						showZoomControls
						disableSyncRef={disableSyncRef}
						documentId={currentDocId}
						documentName={currentDocName}
						initialSelectedContent={leftInitialSelectedContent}
						scrollLocked={scrollLocked}
						scrollRef={leftScrollRef}
						scrollToPage={leftScrollTarget?.page}
						showReturnToContent={false}
						siblingPageDimensions={rightPageDimensions}
						siblingScrollRef={rightScrollRef}
						siblingZoom={sharedZoom}
						versionLabel={translate(
							"common:cmp-details.option-panel.versioning.comparison.current-version"
						)}
						zoom={sharedZoom}
						onContentClick={handleLeftContentClick}
						onPageDimensionsChange={handleLeftPageDimensionsChange}
						onScrollToContent={null}
						onZoomChange={handleZoomChange}
					/>
					<div className={styles.divider} />
					<ComparisonDocumentViewer
						onlyShowContent
						showPageInfo
						showVersionInfo
						showZoomControls
						disableSyncRef={disableSyncRef}
						documentId={previousDocId}
						documentName={previousDocName}
						headerExtra={
							previousDocId && projectId ? (
								<CustomButtonLink
									openInNewTab
									buttonClassName={styles.openBtn}
									color="secondary"
									size="md"
									state={location?.state}
									to={NavUtils.goToSmartReview({
										projectId,
										documentId: previousDocId,
										pageStart: initialRightPage || 1,
										infoId: initialRightInfoId || null,
										companyId,
									})}
									variant="outlined"
								>
									{translate("common:component.preview.open-document")}
								</CustomButtonLink>
							) : null
						}
						initialSelectedContent={rightInitialSelectedContent}
						isLatestVersion={false}
						scrollLocked={scrollLocked}
						scrollRef={rightScrollRef}
						scrollToPage={rightScrollTarget?.page}
						showReturnToContent={false}
						siblingPageDimensions={leftPageDimensions}
						siblingScrollRef={leftScrollRef}
						siblingZoom={sharedZoom}
						versionLabel={translate(
							"common:cmp-details.option-panel.versioning.comparison.previous-version"
						)}
						zoom={sharedZoom}
						onContentClick={handleRightContentClick}
						onPageDimensionsChange={handleRightPageDimensionsChange}
						onScrollToContent={null}
						onZoomChange={handleZoomChange}
					/>
				</div>
			</div>
		</SidePanel>
	);
}
